import { Controller } from "@hotwired/stimulus"

let activeController
const MAX_READY_BYTES = 16 * 1024 * 1024
const RETRY_DELAY = 30000

export default class extends Controller {
  connect() {
    if (this.running) return
    this.records ||= new Map()
    this.operations ||= new Map()
    this.ready ||= new Map()
    if (!this.sweep) this.sweepDone = true
    this.readyBytes = 0
    this.generation = (this.generation || 0) + 1
    this.running = true
    this.onKey = event => { if (event.key === "Escape") this.dismiss() }
    this.onCache = () => this.disconnect()
    this.onDismiss = () => this.dismiss()
    this.onResume = () => this.schedule()
    document.addEventListener("keydown", this.onKey)
    document.addEventListener("turbo:before-cache", this.onCache)
    document.addEventListener("visibilitychange", this.onResume)
    window.addEventListener("online", this.onResume)
    window.addEventListener("offline", this.onResume)
    window.addEventListener("resize", this.onDismiss)
    window.addEventListener("scroll", this.onDismiss, true)
    this.resultsChanged()
  }

  record(url) {
    let record = this.records.get(url)
    if (!record) {
      record = { status: "pending", present: true }
      this.records.set(url, record)
      // Map iterators see additions until exhausted. Restart only for new work,
      // never for ordinary sorting/filtering of already discovered URLs.
      if (!this.sweep || this.sweepDone) {
        this.sweep = this.records.entries()
        this.sweepDone = false
      }
    }
    return record
  }

  resultsChanged() {
    this.dismiss()
    this.rescan = true
    this.schedule() // The results event precedes the actual DOM mutation.
  }

  backgroundAllowed() {
    return !document.hidden && navigator.onLine !== false
  }

  schedule() {
    if (!this.running || this.task != null || !this.backgroundAllowed() && !this.demand) return
    const callback = () => { this.task = null; this.work() }
    this.idleTask = typeof window.requestIdleCallback === "function"
    this.task = this.idleTask ? window.requestIdleCallback(callback, { timeout: 250 }) : window.setTimeout(callback, 25)
  }

  work() {
    if (!this.running) return
    if (!this.backgroundAllowed()) { this.admit(); return }
    const start = performance.now()
    // Discovery, reconciliation AND walking completed ledger entries share a
    // slice. Even a huge already-warmed list cannot monopolize one callback.
    for (let count = 0; count < 100 && performance.now() - start < 4; count++) {
      if (!this.discovery && !this.reconcile && this.rescan && !this.scanComplete) {
        this.rescan = false
        this.scanEpoch = (this.scanEpoch || 0) + 1
        this.discovery = document.createTreeWalker(this.element, NodeFilter.SHOW_ELEMENT)
      }
      if (this.discovery) {
        const node = this.discovery.nextNode()
        if (node) {
          const url = node.dataset.ckImageUrl
          if (url) this.record(url).seen = this.scanEpoch
        } else {
          this.discovery = null
          this.reconcile = this.records.entries()
        }
      } else if (this.reconcile) {
        const item = this.reconcile.next()
        if (item.done) { this.reconcile = null; this.scanComplete = true }
        else {
          const [url, record] = item.value
          const present = record.seen === this.scanEpoch
          if (present && !record.present && record.status === "pending") {
            this.sweep = this.records.entries()
            this.sweepDone = false
          }
          record.present = present
          if (!present) this.evict(url)
        }
      } else if (!this.candidate && !this.sweepDone && this.sweep) {
        const item = this.sweep.next()
        if (item.done) this.sweepDone = true
        else if (item.value[1].present && item.value[1].status === "pending") this.candidate = item.value[0]
      } else break
    }
    const found = this.candidate || this.sweepDone
    this.admit()
    // Give the stable sweep a turn between coalesced passes, even if results
    // events keep arriving. Searching completed ledger entries also yields.
    if (found) this.scanComplete = false
    const scanning = this.discovery || this.reconcile || this.rescan
    const seeking = !this.candidate && !this.sweepDone && this.sweep
    if (scanning || seeking) this.schedule()
  }

  show(event) {
    if (!this.running || event.pointerType === "touch") return
    const anchor = event.currentTarget
    if (this.anchor === anchor) return
    activeController?.dismiss()
    this.dismiss()
    activeController = this
    this.anchor = anchor
    this.url = anchor.dataset.ckImageUrl
    const popup = document.createElement("div")
    popup.className = "ck-product-image-popup"
    popup.setAttribute("role", "status")
    popup.addEventListener("pointerleave", e => this.leave(e))
    this.popup = popup
    document.body.appendChild(popup)
    const cached = this.ready.get(this.url)
    if (cached) {
      this.ready.delete(this.url)
      this.ready.set(this.url, cached)
      this.display(cached.image) // Exact ready node, without a Loading assignment.
      return
    }
    const record = this.url && this.record(this.url)
    if (!record || (record.status === "failed" && Date.now() - record.failedAt < RETRY_DELAY)) {
      this.unavailable()
      return
    }
    popup.textContent = "Loading Card Kingdom image…"
    this.position()
    this.demand = this.url
    this.admit()
    this.schedule()
  }

  current(url) {
    return this.running && this.popup && this.url === url && this.anchor?.isConnected &&
      this.element.contains(this.anchor) && this.anchor.dataset.ckImageUrl === url && !this.anchor.closest("[hidden]")
  }

  admit() {
    if (!this.running || this.operations.size >= 2) return
    if (this.demand && !this.current(this.demand)) this.demand = null
    if (this.demand) {
      const record = this.records.get(this.demand)
      if (record.status === "failed" && Date.now() - record.failedAt < RETRY_DELAY) {
        this.demand = null
        this.unavailable()
      }
    }
    const background = [...this.operations.values()].some(operation => operation.background)
    const sweepAvailable = this.backgroundAllowed() && !background &&
      (this.candidate || !this.sweepDone || this.discovery || this.rescan || this.reconcile)
    // After one demand-only admission, reserve the next admission for the
    // oldest unswept URL, including while a cooperative scan is finding it.
    const reserve = sweepAvailable && this.demandSinceBackground
    if (this.demand && !this.operations.has(this.demand) && !reserve) {
      const url = this.demand
      this.demand = null
      if (!background) this.demandSinceBackground = true
      this.start(url, false)
      this.schedule()
    } else if (sweepAvailable && this.candidate && !this.discovery && !this.reconcile) {
      const url = this.candidate
      this.candidate = null
      const record = this.records.get(url)
      if (record.present && record.status === "pending") {
        this.demandSinceBackground = false
        this.start(url, true)
      }
      this.schedule()
    }
    // Never recursively drain slots, even with synchronous native callbacks.
  }

  start(url, background) {
    const record = this.record(url)
    const operation = { url, background, generation: this.generation, settled: false, image: null }
    this.operations.set(url, operation)
    record.status = "loading"
    try {
      operation.image = new Image()
      operation.image.decoding = "async"
      operation.image.onload = () => this.loaded(operation)
      operation.image.onerror = () => { if (!operation.decoding) this.settle(operation, false) }
      operation.image.src = url
    } catch (_) {
      this.settle(operation, false)
    }
  }

  loaded(operation) {
    if (operation.settled || operation.decoding) return
    const image = operation.image
    const bytes = image.naturalWidth * image.naturalHeight * 4
    if (!Number.isFinite(bytes) || bytes <= 0) { this.settle(operation, false); return }
    operation.bytes = bytes
    const usable = this.running && operation.generation === this.generation
    const wanted = usable && this.current(operation.url)
    const room = this.ready.size < 8 && this.readyBytes + bytes <= MAX_READY_BYTES
    if (usable && (wanted || room) && typeof image.decode === "function") {
      operation.decoding = true
      try {
        // Closures hold the operation only; settlement clears its Image ref.
        image.decode().then(() => this.settle(operation, true), () => this.settle(operation, false))
      } catch (_) { this.settle(operation, false) }
    } else this.settle(operation, true)
  }

  settle(operation, success) {
    if (operation.settled) return
    operation.settled = true
    const image = operation.image
    operation.image = null
    if (image) { image.onload = null; image.onerror = null }
    if (this.operations.get(operation.url) === operation) this.operations.delete(operation.url)
    const record = this.records.get(operation.url)
    record.status = success ? "loaded" : "failed"
    record.failedAt = success ? null : Date.now()
    const usable = this.running && operation.generation === this.generation
    const wanted = usable && this.current(operation.url)
    if (success && usable && record.present) this.retain(operation.url, image, operation.bytes, wanted)
    if (wanted) {
      if (success) this.display(image)
      else this.unavailable()
    }
    if (this.demand === operation.url && usable) this.demand = null
    // Accounting always settles, including stale generations. Only the current
    // scheduler may resume; disposed instances never start replacement work.
    if (this.running) this.schedule()
  }

  retain(url, image, bytes, wanted) {
    if (bytes > MAX_READY_BYTES) return
    if (wanted) {
      for (const [key, entry] of this.ready) {
        if (this.ready.size < 8 && this.readyBytes + bytes <= MAX_READY_BYTES) break
        if (entry.image !== this.popup?.firstChild) this.evict(key)
      }
    }
    if (this.ready.size < 8 && this.readyBytes + bytes <= MAX_READY_BYTES) {
      this.ready.set(url, { image, bytes })
      this.readyBytes += bytes
    }
  }

  evict(url) {
    const entry = this.ready.get(url)
    if (!entry) return
    this.readyBytes -= entry.bytes
    this.ready.delete(url)
  }

  display(image) {
    image.alt = this.anchor.dataset.ckImageLabel || "Card Kingdom listing"
    this.popup.replaceChildren(image)
    this.position()
  }

  unavailable() {
    this.popup.textContent = "Card Kingdom image unavailable"
    this.position()
  }

  position() {
    const anchor = this.anchor.getBoundingClientRect()
    const popup = this.popup.getBoundingClientRect()
    // Measure after content changes so short messages stay beside the name,
    // while images capped by CSS fit entirely inside the viewport.
    this.popup.style.left = `${Math.max(0, Math.min(anchor.right, window.innerWidth - popup.width))}px`
    this.popup.style.top = `${Math.max(0, Math.min(anchor.top, window.innerHeight - popup.height))}px`
  }

  leave(event) {
    const next = event.relatedTarget
    if (next && (this.anchor?.contains(next) || this.popup?.contains(next))) return
    // Pointer departure should not close a keyboard-focused name.
    if (event.type === "pointerleave" && this.anchor?.contains(document.activeElement)) return
    this.dismiss()
  }

  dismiss() {
    this.popup?.replaceChildren()
    this.popup?.remove()
    this.popup = null
    this.anchor = null
    this.url = null
    this.demand = null
    if (activeController === this) activeController = null
  }

  disconnect() {
    if (!this.running) return
    this.running = false
    this.generation += 1
    this.dismiss()
    if (this.task != null) {
      if (this.idleTask) window.cancelIdleCallback(this.task)
      else window.clearTimeout(this.task)
    }
    this.task = null
    this.discovery = null
    this.reconcile = null
    this.scanComplete = false
    this.ready.clear()
    this.readyBytes = 0
    document.removeEventListener("keydown", this.onKey)
    document.removeEventListener("turbo:before-cache", this.onCache)
    document.removeEventListener("visibilitychange", this.onResume)
    window.removeEventListener("online", this.onResume)
    window.removeEventListener("offline", this.onResume)
    window.removeEventListener("resize", this.onDismiss)
    window.removeEventListener("scroll", this.onDismiss, true)
  }
}
